import React from "react";
import { CSVLink } from "react-csv";
import { useLocation } from "react-router-dom";

import AssessmentList from "../../../templates/teacher/AssessmentList";
import {
  Context,
  fetchAssessmentStudents,
  fetchDataAssessmentCSV,
  updateAssessmentLastActive,
  fetchTemporaryToken,
  clearTemporaryToken,
  fetchAssessmentPast,
  fetchAssessment,
  sendFeedbackComment,
  updateAssessmentReport,
} from "../../../teacherStore";
import {
  CSV_HEADERS,
  SCORE_LABELS,
} from "../../../constants/assessment-content";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import useUpdateAssessmentLastActive from "../../../hooks/useUpdateAssessmentLastActive";
import { NextActionKind } from "../../../constants/enum";
import { INextAction } from "../../../state";

export type ISimpleStudent = {
  id: number;
  full_name: string;
};

const convertScoreToName = (score?: number | null) => {
  switch (score) {
    case 1:
      return SCORE_LABELS.label1;
    case 2:
      return SCORE_LABELS.label2;
    case 3:
      return SCORE_LABELS.label3;
    case 4:
      return SCORE_LABELS.label4;
    case 5:
      return SCORE_LABELS.label5;
    case 6:
      return SCORE_LABELS.label6;
    case 7:
      return SCORE_LABELS.label7;
    case 8:
      return SCORE_LABELS.label8;
    default:
      return "";
  }
};

export default () => {
  const { state, dispatch } = React.useContext(Context);
  const location = useLocation();
  const classIDFromSearchParams = new URLSearchParams(location.search).get(
    "class_id",
  );
  const [calling, peep] = useWaitApiCall(state);
  const classes = React.useMemo(
    () => state.teacher.classes.filter(cls => !cls.is_sub),
    [state.teacher.classes],
  );
  const [values, updateValues] = React.useState({
    class_id: 0,
  });
  const [currentStudent, setCurrentStudent] = React.useState<ISimpleStudent>({
    id: 0,
    full_name: "",
  });

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const update = (args: { [key: string]: any }) => {
    updateValues(prevValues => ({ ...prevValues, ...args }));
  };

  React.useEffect(() => {
    if (currentStudent.id && values.class_id) {
      dispatch(peep(fetchAssessmentPast(currentStudent.id, values.class_id)));
      dispatch(peep(fetchAssessment(currentStudent.id, values.class_id)));
    }
  }, [values.class_id, dispatch, peep, currentStudent.id]);

  const fetchAssessmentStudentsFunc = React.useCallback(
    (class_id: number) => {
      dispatch(peep(fetchAssessmentStudents(Number(class_id))));
    },
    [dispatch, peep],
  );

  const handleDownloadCSV = React.useCallback(
    (class_id: number) => {
      dispatch(peep(fetchDataAssessmentCSV(class_id)));
    },
    [dispatch, peep],
  );

  const getNextActionsByKind = (
    kind: NextActionKind,
    nextActions: INextAction[],
  ) => {
    return nextActions
      .filter(n => n.kind === kind)
      .map(item => item.next_action)
      .join("\r\n");
  };

  useUpdateAssessmentLastActive(
    state.teacher.assessment_last_active_date ?? null,
    dispatch,
    updateAssessmentLastActive,
  );

  const csvData = React.useMemo(
    () =>
      state.assessmentsCSV.length
        ? state.assessmentsCSV.map(assessment =>
            [
              assessment.grade,
              assessment.class_name,
              assessment.created_at,
              assessment.term_no,
              assessment.student.identity,
              assessment.student.student_no_in_school,
              assessment.student.full_name,
              convertScoreToName(assessment.assessment_experiences[0].score),
              assessment.assessment_experiences[0].reasons
                .map(item => item.reason)
                .join("\r\n"),
              convertScoreToName(assessment.assessment_experiences[1].score),
              assessment.assessment_experiences[1].reasons
                .map(item => item.reason)
                .join("\r\n"),
              convertScoreToName(assessment.assessment_experiences[2].score),
              assessment.assessment_experiences[2].reasons
                .map(item => item.reason)
                .join("\r\n"),
              convertScoreToName(assessment.assessment_experiences[3].score),
              assessment.assessment_experiences[3].reasons
                .map(item => item.reason)
                .join("\r\n"),
              convertScoreToName(assessment.assessment_experiences[4].score),
              assessment.assessment_experiences[4].reasons
                .map(item => item.reason)
                .join("\r\n"),
              convertScoreToName(assessment.assessment_experiences[5].score),
              assessment.assessment_experiences[5].reasons
                .map(item => item.reason)
                .join("\r\n"),
              convertScoreToName(assessment.assessment_experiences[6].score),
              assessment.assessment_experiences[6].reasons
                .map(item => item.reason)
                .join("\r\n"),
              getNextActionsByKind(
                NextActionKind.KIND_1,
                assessment.assessment_next_actions,
              ),
              getNextActionsByKind(
                NextActionKind.KIND_3,
                assessment.assessment_next_actions,
              ),
              getNextActionsByKind(
                NextActionKind.KIND_2,
                assessment.assessment_next_actions,
              ),
              assessment.feedback_comment,
            ].map(value =>
              typeof value === "string" ? value.replaceAll('"', '""') : value,
            ),
          )
        : [],
    [state.assessmentsCSV],
  );

  useDidMountEffect(() => {
    const timeout = setTimeout(() => {
      csvData.length &&
        csvLinkRef.current?.link &&
        csvLinkRef.current.props.data.length &&
        csvLinkRef.current.link.click();
    });
    return () => clearTimeout(timeout);
  }, [csvLinkRef, csvData]);

  React.useEffect(() => {
    if (classIDFromSearchParams)
      update({ class_id: Number(classIDFromSearchParams) });
    else update({ class_id: classes.length > 0 ? classes[0].id : 0 });
  }, [classIDFromSearchParams, classes]);

  const handleDownloadPDF = React.useCallback(() => {
    dispatch(peep(fetchTemporaryToken()));
  }, [dispatch, peep]);

  const handleUpdateAssessmentsReport = React.useCallback(
    (class_id: number) => {
      dispatch(peep(updateAssessmentReport(class_id)));
    },
    [dispatch, peep],
  );

  const handleClearPDFToken = React.useCallback(() => {
    dispatch(clearTemporaryToken());
  }, [dispatch]);

  const handleSubmitComment = React.useCallback(
    (feedback_comment: string) => {
      if (calling) return;
      return dispatch(
        peep(sendFeedbackComment(currentStudent.id, feedback_comment)),
      );
    },
    [calling, dispatch, peep, currentStudent.id],
  );

  return classes ? (
    <>
      <AssessmentList
        calling={calling}
        class_id={values.class_id}
        classIDFromSearchParams={
          Number(classIDFromSearchParams) ||
          (classes.length > 0 ? classes[0].id : 0)
        }
        classes={classes}
        pdfToken={state.temporaryToken}
        currentStudent={currentStudent}
        updateCurrentStudent={setCurrentStudent}
        update={update}
        assessmentStudents={state.assessmentStudents}
        assessment={state.assessment}
        assessments_past={state.assessments_past}
        assessment_answers={state.assessmentAnswers}
        referenceScores={state.assessmentReferenceScores}
        fetchAssessmentStudents={fetchAssessmentStudentsFunc}
        handleSubmitComment={handleSubmitComment}
        onDownloadCSV={handleDownloadCSV}
        onUpdateAssessmentsReport={handleUpdateAssessmentsReport}
        onDownloadPDF={handleDownloadPDF}
        onClearPDFToken={handleClearPDFToken}
      />
      <CSVLink
        ref={csvLinkRef}
        filename={`glide_download_${
          classes.find(cl => cl.id === values.class_id)?.full_name
        }.csv`}
        headers={CSV_HEADERS}
        data={csvData}
        uFEFF
        hidden
      />
    </>
  ) : null;
};
